<script>
import { mapGetters } from 'vuex'
import { GlobalBus } from '@services/bus'
import { CHAT_IA_EVENTOS, FEATURES, MODAL } from '@constants/Text'
import {
  CONVERSA_STATUS_OPTIONS,
  ICONS,
  RESUMO_NAO_LIDAS_ERRO,
} from '@modules/atendimento/constants/Text'
import {
  apiPerguntasPredefinidas,
  PERGUNTA_RESUMO_NAO_LIDAS,
} from '@modules/aawz-intelligence-v2/api/apiPerguntasPredefinidas'
import { parseBackendDate } from '@modules/atendimento/utils/message-formatter'
import {
  matchesConversaSearch,
  temNaoLidas,
} from '@modules/atendimento/utils/conversa-helpers'
import { deveMostrarChatIaWidget } from '@modules/global/utils/chat-ia-widget-visivel'
import { hexToRgb } from '@modules/global/utils/Color'
import ConversationListItem from '@modules/atendimento/components/conversation-list-item/conversation-list-item.vue'
import ConexaoActionsMenu from '@modules/atendimento/components/conexao-actions-menu/conexao-actions-menu.vue'
import ConexaoQrcodeAnimatedSvg from '@modules/atendimento/components/conexao-qrcode-animated-svg/conexao-qrcode-animated-svg.vue'
import {
  resolveConexaoStatusCta,
  CONEXAO_CTA_TYPE,
} from '@modules/atendimento/utils/conexao-status-message'

export default {
  name: 'ConversationList',
  components: {
    ConversationListItem,
    ConexaoActionsMenu,
    ConexaoQrcodeAnimatedSvg,
  },
  props: {
    conexoes: {
      type: Array,
      default() {
        return []
      },
    },
    // Se true, o empty state "lista vazia" (conversas.length === 0)
    // ganha CTA "Nova Conversa" + link pra configurar conexões WhatsApp —
    // mesmo conjunto da empty state do chat-window (view /atendimento).
    // Usado pelo popup no CRM pra dar UX completa num painel único.
    emptyStateRich: {
      type: Boolean,
      default: false,
    },
    panelStyle: {
      type: String,
      default: 'flatGray',
      validator: (v) => ['flatGray', 'flatWhite'].includes(v),
    },
    // Controlada pelo pai (view-atendimento / atendimento-popup). O pai
    // gerencia o ciclo do refresh em N conexões via
    // fireRefreshChatsListForVisible. ConversationList apenas repassa
    // a prop para o ConexaoActionsMenu (que renderiza o item
    // "Recarregar conversas" dentro do menu ⋮) e re-emite o evento
    // refresh-chats-list para o pai.
    refreshing: {
      type: Boolean,
      default: false,
    },
    // Valor do filtro de conexão controlado pelo pai (view-atendimento), que
    // restaura a última conexão salva em localStorage após o load assíncrono.
    // Default null = "Todas as conexões" — popup/omni não passam a prop e
    // mantêm o comportamento atual.
    conexaoFilterValue: {
      type: [Number, String],
      default: null,
    },
    // Scroll infinito: true enquanto carrega próxima página (loading sutil
    // no fim da lista, não o skeleton cheio).
    loadingMore: {
      type: Boolean,
      default: false,
    },
    // Scroll infinito: false quando não há mais páginas (última página).
    // Default false = sem scroll infinito até que o pai informe explicitamente
    // que há páginas seguintes (view-atendimento sempre passa :has-more).
    hasMore: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      preparandoResumo: false,
      icons: ICONS,
      skeletonWidths: [
        [120, 180],
        [85, 140],
        [140, 100],
        [95, 160],
        [110, 130],
        [130, 110],
        [75, 170],
        [145, 90],
        [100, 150],
        [90, 120],
      ],
      searchText: '',
      statusFilter: null,
      conexaoFilter: this.conexaoFilterValue,
      conexaoExpanded: false,
      tipoFilter: 'todas',
      // Resolved once in mounted() — avoids re-running getComputedStyle
      // on every render. Primary color is tied to the office theme and
      // does not change at runtime within a session.
      conexaoBarBg: 'rgba(0, 0, 0, 0.06)',
      tutorialMenuActive: false,
    }
  },
  computed: {
    // Botão "Resumir não lidas com a IA": flag própria do escritório
    // (`IA - WhatsApp Resumo`, opt-in) + a MESMA régua do popup que vai
    // receber a pergunta (feature do chat + rota — na /ia e na imersiva o
    // popup não existe e o clique seria um no-op silencioso) + algo para resumir.
    podeResumirComIa() {
      return (
        !!(this.$access || {})[FEATURES.IA_WHATSAPP_RESUMO] &&
        deveMostrarChatIaWidget({
          routeName: this.$route && this.$route.name,
          user: this.$currentUser(),
          access: this.$access,
        }) &&
        temNaoLidas(this.conversas)
      )
    },
    ...mapGetters('atendimento', [
      'conversas',
      'filtroStatus',
      'selectedConversa',
      'loadingConversas',
    ]),
    // Lista realmente vazia (sem dados) — distinto de "filtrou e não
    // achou". Usado no empty state rich pra decidir entre CTA + link
    // de config vs. "nenhuma conversa encontrada".
    hasAnyConversas() {
      return (this.conversas || []).length > 0
    },
    hasActiveConexoes() {
      return (this.conexoes || []).some((c) => c && c.status === 'conectado')
    },
    isGestor() {
      const user = this.$currentUser() || {}
      return !!user.IS_GESTOR
    },
    conexaoCta() {
      return resolveConexaoStatusCta({
        conexoes: this.conexoes,
        isGestor: this.isGestor,
      })
    },
    ctaTypes() {
      return CONEXAO_CTA_TYPE
    },
    statusOptions() {
      return CONVERSA_STATUS_OPTIONS
    },
    // Só considera conexões conectadas — filtrar por uma conexão
    // desconectada não traria conversas novas e confunde o usuário.
    connectedConexoes() {
      return (this.conexoes || []).filter((c) => c && c.status === 'conectado')
    },
    // Filtro de conexão só faz sentido com 2+ conexões conectadas.
    // Com 0 ou 1 não há o que alternar.
    showConexaoFilter() {
      return this.connectedConexoes.length >= 2
    },
    conexaoOptions() {
      const options = [{ text: 'Todas as conexões', value: null }]
      for (const conn of this.connectedConexoes) {
        options.push({
          text: conn.nome || 'Conexão #' + conn.id,
          value: conn.id,
        })
      }
      return options
    },
    // Remove conversas órfãs: após "Remover permanentemente" uma conexão,
    // o backend apaga a conexão imediatamente mas pode demorar segundos
    // pra limpar as conversas associadas. Sem esse filtro, a UI fica
    // mostrando conversas fantasmas até o backend completar. Se `conexoes`
    // ainda não carregou (array vazio), não filtra pra não esconder tudo.
    conversasVivas() {
      const list = this.conversas || []
      if (!this.conexoes || this.conexoes.length === 0) return list
      const validIds = new Set(this.conexoes.map((c) => c && c.id))
      return list.filter((c) => !c.id_conexao || validIds.has(c.id_conexao))
    },
    filteredByConexao() {
      if (!this.conexaoFilter) return this.conversasVivas
      return this.conversasVivas.filter(
        (c) => c.id_conexao === this.conexaoFilter
      )
    },
    filteredByStatus() {
      if (!this.statusFilter) return this.filteredByConexao
      return this.filteredByConexao.filter(
        (c) => c.status === this.statusFilter
      )
    },
    filteredByTipo() {
      const tipo = this.tipoFilter
      if (tipo === 'conversas') {
        return this.filteredByStatus.filter(
          (c) => !(c.contato && c.contato.is_group)
        )
      }
      if (tipo === 'grupos') {
        return this.filteredByStatus.filter(
          (c) => !!(c.contato && c.contato.is_group)
        )
      }
      return this.filteredByStatus
    },
    filteredConversas() {
      const search = (this.searchText || '').toLowerCase().trim()
      if (!search) return this.filteredByTipo
      return this.filteredByTipo.filter((c) => matchesConversaSearch(c, search))
    },
    sortedConversas() {
      const list = this.filteredConversas.slice()
      list.sort((a, b) => {
        const dA = a.ultima_mensagem_em
          ? parseBackendDate(a.ultima_mensagem_em)
          : null
        const dB = b.ultima_mensagem_em
          ? parseBackendDate(b.ultima_mensagem_em)
          : null
        const dateA = dA ? dA.getTime() : 0
        const dateB = dB ? dB.getTime() : 0
        return dateB - dateA
      })
      return list
    },
    hasConversas() {
      return this.sortedConversas.length > 0
    },
    tiposFiltro() {
      return [
        { value: 'todas', text: 'Todas' },
        { value: 'conversas', text: 'Conversas' },
        { value: 'grupos', text: 'Grupos' },
      ]
    },
    selectedConexaoName() {
      if (!this.conexaoFilter) return 'Todas as conexões'
      const opt = this.conexaoOptions.find(
        (o) => o.value === this.conexaoFilter
      )
      return opt ? opt.text : 'Todas as conexões'
    },
  },
  watch: {
    // Sincroniza o filtro local quando o pai restaura a conexão salva
    // (localStorage) após o load assíncrono das conexões. Sem loop: o valor
    // restaurado é igual ao que onConexaoFilter emitiu de volta ao pai.
    conexaoFilterValue(value) {
      if (value !== this.conexaoFilter) this.conexaoFilter = value
    },
    // Scroll infinito: quando as conversas carregam (passam de vazio pra
    // preenchido), o elemento .conversation-list__items só existe no DOM
    // quando hasConversas é true. O attach em mounted pode falhar se as
    // conversas ainda estão carregando — o watcher tenta novamente aqui.
    hasConversas(has) {
      if (has) this._attachScrollListener()
    },
  },
  mounted() {
    this.resolveConexaoBarBg()
    this._attachScrollListener()
  },
  beforeDestroy() {
    const el = this.$el ? this.$el.querySelector('.conversation-list__items') : null
    if (el) {
      el.removeEventListener('scroll', this.onScrollList)
    }
  },
  methods: {
    // O prompt vem do backend pela chave; o popup da IA (sempre montado no
    // layout) abre e envia o texto como pergunta.
    async resumirNaoLidasComIa() {
      if (this.preparandoResumo) return
      this.preparandoResumo = true
      const r = await apiPerguntasPredefinidas.obter(PERGUNTA_RESUMO_NAO_LIDAS)
      this.preparandoResumo = false
      const texto = r && r.status === 200 && r.value && r.value.texto
      if (!texto) {
        this.$showSnack({ text: RESUMO_NAO_LIDAS_ERRO, color: 'error', button: true })
        return
      }
      GlobalBus.$emit(CHAT_IA_EVENTOS.ABRIR_COM_PERGUNTA, { pergunta: texto, enviar: true })
    },
    resolveConexaoBarBg() {
      const primary = getComputedStyle(document.documentElement)
        .getPropertyValue('--primary')
        .trim()
      if (!primary) return
      const [r, g, b] = hexToRgb(primary)
      this.conexaoBarBg = `rgba(${r}, ${g}, ${b}, 0.10)`
    },
    // Attach scroll listener no .conversation-list__items. O elemento só
    // existe no DOM quando hasConversas é true (v-else-if). Remove o listener
    // antigo antes de adicionar pra evitar duplicatas quando hasConversas
    // oscila entre true e false (troca de filtro).
    _attachScrollListener() {
      this.$nextTick(() => {
        const el = this.$el
          ? this.$el.querySelector('.conversation-list__items')
          : null
        if (el) {
          el.removeEventListener('scroll', this.onScrollList)
          el.addEventListener('scroll', this.onScrollList, { passive: true })
        }
      })
    },
    // Scroll infinito: quando o usuário atinge 90% do fim da lista,
    // emite load-more para o pai carregar a próxima página.
    onScrollList(event) {
      const el = event.target
      if (!el || !this.hasMore || this.loadingMore) return
      const threshold = 0.9
      const atBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight * threshold
      if (atBottom) {
        this.$emit('load-more')
      }
    },
    onSearch(text) {
      this.searchText = text
      this.$emit('search', this.searchText)
    },
    onSelectConversa(conversa) {
      this.$emit('select', conversa)
    },
    onNovaConversa() {
      GlobalBus.$emit(MODAL.ATENDIMENTO_NOVA_CONVERSA)
    },
    // Re-emite o evento do ConexaoActionsMenu para o pai
    // (view-atendimento / atendimento-popup) que detém o handler
    // onRefreshChatsListFromList e o estado refreshingChats.
    onRefreshChatsListFromMenu() {
      this.$emit('refresh-chats-list')
    },
    onStatusFilter(value) {
      this.statusFilter = value
      this.$store.dispatch('atendimento/setFiltroStatus', value)
      this.$emit('filter-status', value)
    },
    onConexaoFilter(value) {
      this.conexaoFilter = value
      this.$emit('filter-conexao', value)
    },
    selectTipo(value) {
      this.tipoFilter = value
    },
    onConexaoUpdated(payload) {
      this.$emit('conexao-updated', payload)
    },
  },
}
</script>

<template>
  <div class="conversation-list d-flex flex-column conversation-list-root">
    <div class="d-flex flex-column align-center justify-center az-gap mb-4">
      <div
        class="d-flex align-center w-100"
        :class="emptyStateRich ? 'justify-space-between' : 'justify-center'"
      >
        <input-by-type
          :value="searchText"
          type="search"
          class="mr-2"
          placeholder="Buscar conversa..."
          data-test="conversation-search"
          clearable
          @input="searchText = $event"
        />

        <div class="d-flex align-center justify-end">
          <v-tooltip v-if="podeResumirComIa" top>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-btn
                  class="nova-conversa-btn mr-1"
                  data-test="resumir-nao-lidas-btn"
                  x-small
                  icon
                  color="primary"
                  fab
                  :loading="preparandoResumo"
                  @click="resumirNaoLidasComIa"
                >
                  <icon-svg name="ai-sparkle" size="16" color="white" />
                </v-btn>
              </span>
            </template>
            <span>Resumir não lidas com a IA</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-btn
                  class="nova-conversa-btn"
                  data-test="nova-conversa-btn"
                  x-small
                  icon
                  color="primary"
                  fab
                  :disabled="!hasActiveConexoes"
                  @click="onNovaConversa"
                >
                  <icon-svg :name="icons.plus" size="16" color="#FFFFFF" />
                </v-btn>
              </span>
            </template>
            <span>
              {{
                hasActiveConexoes
                  ? 'Nova conversa'
                  : 'Conecte um WhatsApp para iniciar conversas'
              }}
            </span>
          </v-tooltip>

          <!-- Menu de ações de conexão (QR Code, reiniciar, desconectar,
               recarregar conversas, etc.). O item "Recarregar conversas"
               é controlado pelas props `refreshing` (loading) e
               `connected-conexoes` (gating). Ao clicar, emite
               `refresh-chats-list` que é re-emitido para o pai
               (view-atendimento / atendimento-popup). -->
          <ConexaoActionsMenu
            :conexoes="conexoes"
            :refreshing="refreshing"
            :connected-conexoes="connectedConexoes"
            @conexao-updated="onConexaoUpdated"
            @refresh-chats-list="onRefreshChatsListFromMenu"
          />
        </div>
      </div>

      <!-- Type toggle: Todas / Conversas / Grupos -->
      <BaseToggleButton
        :value="tipoFilter"
        :options="tiposFiltro"
        :buttonStyle="panelStyle"
        small
        class="toggle-full-width"
        @input="selectTipo"
      />
    </div>

    <div
      v-if="showConexaoFilter"
      class="conexao-filter-bar mb-4"
      :style="{ backgroundColor: conexaoBarBg }"
      data-test="conexao-filter-bar"
    >
      <div
        class="conexao-filter-bar__header d-flex align-center px-3 py-2"
        @click="conexaoExpanded = !conexaoExpanded"
      >
        <icon-svg
          :name="icons.connections"
          size="14"
          color="primary"
          class="mr-2"
        />
        <span class="az-text-small font-medium flex-grow-1 text-truncate">
          {{ selectedConexaoName }}
        </span>
        <icon-svg
          :name="conexaoExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          size="16"
        />
      </div>
      <v-expand-transition>
        <div v-show="conexaoExpanded" class="px-3 pb-2">
          <input-by-type
            type="autocomplete"
            :value="conexaoFilter"
            :options="conexaoOptions"
            class="align-icon-search"
            dense
            hide-details
            data-test="conexao-filter"
            @input="onConexaoFilter"
          />
        </div>
      </v-expand-transition>
    </div>

    <div v-if="loadingConversas && !hasConversas" class="flex-grow-1 pb-2">
      <div
        v-for="i in 10"
        :key="'skeleton-' + i"
        class="d-flex align-center pa-2 mb-1"
      >
        <BaseLoadingSkeleton
          width="32px"
          height="32px"
          style="border-radius: 999px"
          class="mr-2 rounded-circle"
        />
        <div class="flex-grow-1">
          <BaseLoadingSkeleton
            :width="skeletonWidths[i - 1][0] + 'px'"
            height="14px"
            class="mb-1"
          />
          <BaseLoadingSkeleton
            :width="skeletonWidths[i - 1][1] + 'px'"
            height="12px"
          />
        </div>
      </div>
    </div>

    <div
      v-else-if="hasConversas"
      class="conversation-list__items overflow-y-auto flex-grow-1"
    >
      <ConversationListItem
        v-for="conversa in sortedConversas"
        :key="conversa.id"
        :conversa="conversa"
        :conexoes="conexoes"
        :selected="selectedConversa && selectedConversa.id === conversa.id"
        @click="onSelectConversa"
      />
      <!-- Loading sutil no fim da lista (scroll infinito) -->
      <div
        v-if="loadingMore"
        class="d-flex align-center justify-center py-3"
        data-test="load-more-indicator"
      >
        <v-progress-circular
          indeterminate
          size="16"
          width="2"
          color="primary"
          class="mr-2"
        />
        <span class="az-text-small az-muted">Carregando mais...</span>
      </div>
    </div>

    <!--
      Empty state rich (usado no popup do CRM): quando há CTA de conexão
      (sem conexão ativa, pedir gestor ou escanear QR), a mensagem do CTA
      vira o destaque — título + descrição explicando o próximo passo.
      Quando a conexão está OK (conexaoCta.type === null), mostra o
      empty state padrão com "Nova Conversa".
    -->
    <div
      v-else-if="emptyStateRich && !hasAnyConversas && conexaoCta.type"
      data-test="empty-state-conexao-cta"
      class="d-flex flex-column align-center justify-center flex-grow-1 px-4 no-conversas-container"
    >
      <icon-svg
        :name="icons.chatOutline"
        size="64"
        color="#8E8E8E"
        class="mb-4"
      />
      <span
        class="az-text-primary az-size-16 font-medium az-muted"
        data-test="conexao-cta-title"
      >
        {{ conexaoCta.title }}
      </span>
      <span
        class="az-text-small text-center mt-1 az-muted empty-state-description"
        data-test="conexao-cta-description"
      >
        {{ conexaoCta.description }}
      </span>
      <router-link
        v-if="conexaoCta.type === ctaTypes.CONFIG"
        :to="{ name: 'settings-v2.atendimento.conexoes' }"
        class="az-text-small primary--text mt-4"
        data-test="conexao-cta-config-link"
      >
        Ir para Configurações
      </router-link>
      <v-menu
        v-else-if="conexaoCta.type === ctaTypes.SCAN_QRCODE"
        v-model="tutorialMenuActive"
        max-width="640"
        top
        open-on-hover
        :close-on-content-click="false"
      >
        <template v-slot:activator="{ on }">
          <span
            class="cursor-help d-inline-flex align-center az-text-small primary--text mt-3"
            v-on="on"
          >
            <icon-svg
              name="mdi-information"
              size="14"
              color="var(--primary)"
              class="mr-1"
            />
            Ver tutorial passo a passo
          </span>
        </template>
        <div class="az-card pa-4">
          <span class="az-text-small">
            Clique no menu de ações <b>⋮</b> no topo da lista de conversas e
            selecione <b>Conectar via QR Code</b>. Um modal abrirá com o código
            para pareamento no WhatsApp.
          </span>
          <v-divider class="my-3" />
          <ConexaoQrcodeAnimatedSvg />
        </div>
      </v-menu>
    </div>

    <div
      v-else-if="emptyStateRich && !hasAnyConversas"
      data-test="empty-state-rich"
      class="d-flex flex-column align-center justify-center flex-grow-1 px-4 no-conversas-container"
    >
      <icon-svg
        :name="icons.chatOutline"
        size="64"
        color="#8E8E8E"
        class="mb-4"
      />
      <span class="az-text-primary az-size-16 font-medium az-muted">
        Selecione ou inicie uma conversa
      </span>
      <span
        class="az-text-small text-center mt-1 az-muted empty-state-description"
      >
        Escolha uma das conversas ao lado, ou toque em
        <strong>"Nova Conversa"</strong>
        no topo da lista para começar uma conversa com qualquer número.
      </span>
      <BaseButton
        primary
        class="mt-4"
        min-width="200px"
        :disabled="!hasActiveConexoes"
        @click="onNovaConversa"
      >
        +
        <span class="ml-1">Nova Conversa</span>
      </BaseButton>
    </div>

    <div
      v-else
      data-test="empty-state"
      class="d-flex flex-column align-center justify-center flex-grow-1 pa-4 no-conversas-container"
    >
      <icon-svg
        :name="icons.messageTextOutline"
        size="48"
        color="#8E8E8E"
        class="mb-3"
      />
      <span class="az-text-primary az-size-14 az-muted text-center">
        Nenhuma conversa encontrada
      </span>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.conversation-list
  background-color: var(--panel-bg)

.conversation-list-root
  height: 100%

.no-conversas-container
  margin-bottom: 128px

.empty-state-description
  max-width: 320px

// Mantém o FAB com fundo primary + ícone branco quando disabled — Vuetify
// remove o background do v-btn disabled e tinge o ícone com rgba preto,
// deixando invisível. Forçamos a aparência "ativa" com opacity reduzida.
.nova-conversa-btn.v-btn--disabled
  background-color: var(--primary) !important
  opacity: 0.5

  ::v-deep .v-icon,
  ::v-deep svg,
  ::v-deep .icon-svg
    color: #FFFFFF !important

.conversation-list__items
  min-height: 0
  background-color: var(--panel-bg)

  &::-webkit-scrollbar
    width: 6px

  &::-webkit-scrollbar-track
    background: var(--panel-bg)

  &::-webkit-scrollbar-thumb
    background: #c4c5c7
    border-radius: 3px

  &::-webkit-scrollbar-thumb:hover
    background: #a0a1a3

.conexao-filter-bar
  border-radius: 8px
  transition: background-color 0.2s ease

.conexao-filter-bar__header
  cursor: pointer
  user-select: none

.toggle-full-width
  width: 100%

  ::v-deep .v-btn
    flex: 1

.w-100
  width: 100%
</style>
